/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { Link } from "@packages/framework";
import type { PostPrayerOnReflect } from "@packages/media";
import type { PostPrayerStep, ReflectionPostPrayer } from "@packages/sdk";
import {
  getURLFromDeeplink,
  useIsKidAccount,
  useRequestUsersMeSettings,
} from "@packages/sdk";
import type { ModalProps, ThemeType } from "@packages/ui";
import {
  Button,
  createModal,
  cssMediaQueryMinWidthTablet,
  DesktopDownloadCTA,
  MobileDownloadCTA,
  Modal,
  numericViewportWidth,
  ShareModal,
  spacing,
  Text,
  Thumbnail,
  useCarousel,
  useIsDesktopViewport,
  useModal,
  zIndices,
} from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ActivitySummary } from "../../data/ActivitySummary.emotion";
import type { PostPrayerContentCarouselRef } from "../../data/PostPrayerContentCarousel.emotion";
import { PostPrayerContentCarousel } from "../../data/PostPrayerContentCarousel.emotion";
import { PostPrayerHighlightsButton } from "./PostPrayerHighlights.emotion";
import { PostPrayerModalContent } from "./PostPrayerModalContent.emotion";
import { PostPrayerRecommendationButton } from "./PostPrayerRecommendations.emotion";
import {
  PostPrayerReflectionBody,
  PostPrayerReflectionButton,
} from "./PostPrayerReflections.emotion";
import {
  PostPrayerSubscriptionBody,
  PostPrayerSubscriptionButton,
} from "./PostPrayerSubscription.emotion";

const carouselItemStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  maxWidth: numericViewportWidth[90],
  minHeight: 270,
  minWidth: 75,
  paddingBlock: spacing.ml,
  paddingInline: spacing.none,
  rowGap: spacing.s,
  width: 404,
};
const highlightsItemStyle: Interpolation = {
  justifyContent: "space-between",
};
const modalStyle: Interpolation = {
  paddingBottom: spacing.l,
  paddingInline: spacing.l,
  paddingTop: `calc(${spacing.l} + ${spacing.xl})`,
  width: "min-content",
  [cssMediaQueryMinWidthTablet]: {
    paddingBottom: spacing.l,
    paddingInline: spacing.l,
    paddingTop: `calc(${spacing.l} + ${spacing.xl})`,
  },
};
const sunkModalStyle: Interpolation = {
  zIndex: zIndices.overlayUnderMenuBar,
};
const sunkOverlayStyle: Interpolation = {
  zIndex: zIndices.overlayUnderMenuBar,
};
const buttonContainerStyle: Interpolation = {
  display: "flex",
  placeContent: "center",
  placeItems: "center",
  width: spacing.full,
};
const textContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  justifyContent: "flex-start",
  marginBottom: spacing.xl,
  textAlign: "center",
};
const downloadDescriptionStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigher,
});
const shareOverlayStyle: Interpolation = {
  zIndex: zIndices.overlayOverModal,
};
const shareModalStyle: Interpolation = {
  zIndex: zIndices.modalOverModal,
};
const pulldownBarStyle: Interpolation = {
  marginTop: spacing.negativeXL,
  [cssMediaQueryMinWidthTablet]: { marginTop: spacing.none },
};

export type PostPrayerModalProps = ModalProps & {
  steps: PostPrayerStep[];
  onReflect: PostPrayerOnReflect;
};

const isOnClickableElement = (e: MouseEvent) => {
  const clicked = e.target as HTMLElement;
  const listened = e.currentTarget as HTMLElement;
  if (["a", "button"].includes(listened.tagName.toLowerCase())) return true;
  if (["a", "button"].includes(clicked.tagName.toLowerCase())) return true;
  let checked = clicked;
  while (checked !== listened && checked !== document.documentElement) {
    if (["a", "button"].includes(checked.tagName.toLowerCase())) return true;
    checked = checked.parentElement;
  }
  return false;
};

export type RenderableItem = {
  id: number;
  type: PostPrayerStep["type"];
  title: string | null;
  element: ReactNode;
};

export const PostPrayerModal = createModal(
  ({ steps, onReflect, ...props }: PostPrayerModalProps) => {
    const analytics = useAnalytics();
    const modal = useModal();
    const t = useTranslations();
    const { isKidAccount } = useIsKidAccount();
    const subCarouselRef = useRef<PostPrayerContentCarouselRef>(null);
    const shareModal = useModal(ShareModal);
    const isDesktop = useIsDesktopViewport();
    const [sink, setSink] = useState<boolean>(false);
    const carouselApi = useCarousel({
      hoverButtons: true,
      size: "xl",
      gap: [32, 32],
      pageOffset: [
        [24, 24],
        [24, 24],
      ],
      showPageButtons: true,
      forceMount: true,
    });
    const { query } = useRequestUsersMeSettings({});

    useEffect(() => {
      if (modal.visible) {
        analytics.postEvent({ event: "post-prayer-seen" });
      }
    }, [modal.visible]);

    const handleDownload = ({ platform }: { platform: "apple" | "google" }) => {
      analytics.track({
        event: HallowAnalyticsEvent.TappedModalDownload,
        properties: {
          option: "download",
          platform,
        },
      });
    };

    const items: RenderableItem[] = useMemo(
      () =>
        steps
          .map((step) => {
            let title: string, button: ReactNode, body: ReactNode;
            switch (step.type) {
              case "for_you":
                title = step.title?.length
                  ? step.title
                  : t("post_prayer_recommendations_title");
                button = (
                  <PostPrayerRecommendationButton
                    step={step}
                    onClick={() => modal.hide()}
                  />
                );
                body = null;
                break;
              case "community_prompt":
              case "prompt":
              case "reflection":
                title = t("post_prayer_reflections_title");
                body = <PostPrayerReflectionBody step={step} />;
                button = (
                  <PostPrayerReflectionButton
                    step={step}
                    onClick={async () => {
                      const returnTo =
                        carouselApi.refParent.current?.scrollLeft;
                      setSink(true);
                      await onReflect({
                        prayer: (step as ReflectionPostPrayer).session,
                      });
                      setSink(false);
                      setTimeout(() => {
                        carouselApi.refParent.current?.scrollTo({
                          left: returnTo,
                          behavior: "instant",
                        });
                      }, 250); // 10 doesn't seem to be enough
                    }}
                  />
                );
                break;
              case "highlights":
                title = t("post_prayer_highlights_title");
                body = (
                  <PostPrayerContentCarousel
                    ref={subCarouselRef}
                    sessions={step.sessions}
                  />
                );
                button = isKidAccount ? null : (
                  <PostPrayerHighlightsButton
                    subCarouselRef={subCarouselRef}
                    step={step}
                  />
                );
                break;
              case "subscription":
                title = null;
                body = <PostPrayerSubscriptionBody />;
                button = (
                  <PostPrayerSubscriptionButton onClick={() => modal.hide()} />
                );
                break;
              case "download":
                title = null;
                body = (
                  <div css={textContainerStyle}>
                    <Text as={"h2"} type={"title"} size={"xl"}>
                      {t("web_post_prayer_download_title")}
                    </Text>
                    <Text
                      as={"p"}
                      type={"body"}
                      size={"m"}
                      css={downloadDescriptionStyle}
                    >
                      {isDesktop
                        ? t("download_banner_description")
                        : t("download_popup_description")}
                    </Text>
                  </div>
                );
                button = isDesktop ? (
                  <DesktopDownloadCTA handleDownload={handleDownload} withQr />
                ) : (
                  <MobileDownloadCTA handleDownload={handleDownload} />
                );
                break;
              case "default":
                if (
                  step.deeplink?.includes("routine") ||
                  step.secondary_deeplink?.includes("routine") ||
                  step.deeplink?.includes("campaigns") ||
                  step.secondary_deeplink?.includes("campaigns")
                ) {
                  // we don't support routines or notes of support right now
                  return null;
                }
                title = step.super_title;
                body = (
                  <>
                    <Thumbnail
                      size={"xs"}
                      imageSrc={{ fallback: step.image_url }}
                      title={step.title}
                    />
                    <Text
                      as={"p"}
                      type={"body"}
                      size={"m"}
                      css={textContainerStyle}
                    >
                      {step.desc}
                    </Text>
                  </>
                );
                if (!step.deeplink && step.secondary_deeplink) {
                  button = (
                    <Link
                      href={getURLFromDeeplink({
                        deeplink: step.secondary_deeplink,
                      })}
                    >
                      <Button>{step.secondary_button_text}</Button>
                    </Link>
                  );
                } else if (step.deeplink) {
                  button = (
                    <Link
                      href={getURLFromDeeplink({ deeplink: step.deeplink })}
                    >
                      <Button>{step.button_text}</Button>
                    </Link>
                  );
                } else {
                  button = null;
                }
                break;
              case "prayer_goal_met":
                title = step.title;
                button = isKidAccount ? (
                  <Button onClick={() => modal.hide()}>
                    {t("general_word_continue")}
                  </Button>
                ) : (
                  <Button
                    onClick={() =>
                      shareModal.show({
                        color: "#000",
                        imgSrc: step.prayer_goal.complete_image_url,
                        title: step.title,
                        description: step.prayer_goal.desc,
                        contentCSS: shareModalStyle,
                        overlayCSS: shareOverlayStyle,
                      })
                    }
                  >
                    {t("post_prayer_share")}
                  </Button>
                );
                body = (
                  <>
                    <img
                      alt={""}
                      src={step.prayer_goal.complete_image_url}
                      width={80}
                      height={80}
                    />
                    <Text as={"p"} type={"title"} size={"m"}>
                      {t("general_word_congrats_exclamation")}
                    </Text>
                    <Text as={"p"} type={"body"} size={"m"}>
                      {step.completion_message}
                    </Text>
                  </>
                );
                break;
              case "streak":
              case "summary":
                if (
                  query.data?.global.is_post_prayer_streak_enabled === "false"
                )
                  return null;
                title = null;
                body = <ActivitySummary postPrayer={step} />;
                button = null;
                break;
              case "intentions":
              case "rating":
              case "signup":
              case "giving_campaign":
              case "gift_hallow":
              case "celebration_activity":
              default:
                return null;
            }

            return {
              id: step.id,
              type: step.type,
              title: title,
              element: (
                <div
                  key={`step${step.id}`}
                  css={[
                    carouselItemStyle,
                    step.type === "highlights" && highlightsItemStyle,
                  ]}
                >
                  {body}
                  {button ? (
                    <div
                      key={"actionWrapper"}
                      css={buttonContainerStyle}
                      onClickCapture={(e) => {
                        if (isOnClickableElement(e.nativeEvent)) {
                          analytics.track({
                            event:
                              HallowAnalyticsEvent.TookPostPrayerScreenAction,
                            properties: {
                              screen: step.id,
                              screen_name: step.type,
                            },
                          });
                          analytics.postEvent({
                            event: `post-prayer-took-action-${step.id}`,
                          });
                        }
                      }}
                    >
                      {button}
                    </div>
                  ) : null}
                </div>
              ),
            };
          })
          .filter((itm) => !!itm),
      [steps, onReflect, isDesktop],
    );

    const handleInteraction = useCallback(
      (e) => {
        if (sink) e.preventDefault();
      },
      [sink],
    );

    return (
      <Modal
        {...props}
        open={modal.visible}
        onOpenChange={(open) => {
          if (open) return modal.show();

          modal.resolve();
          return modal.hide();
        }}
        contentCSS={[modalStyle, sink && sunkModalStyle]}
        overlayCSS={[sink && sunkOverlayStyle]}
        hasMobilePulldownBar={!isDesktop}
        showClose
        modal={!sink}
        contentProps={{
          onInteractOutside: handleInteraction,
          "data-testid": HallowDataTestId.PostPrayerModal,
        }}
        closeCSS={pulldownBarStyle}
      >
        <PostPrayerModalContent items={items} carouselApi={carouselApi} />
      </Modal>
    );
  },
);
